import { useCallback, useEffect, useReducer, useState } from 'react';
import { arrayFilter, arrayPages } from '../../../../../../common/filtering';
import { FetchRefreshFunctionParams } from '../../../../../../common/useFetch';
import useListFetch from '../../../../../../common/useListFetch';
import CmonProxysqlService from '../../../../../../services/cmon/CmonProxysqlService';
import {
    initialListState,
    listReducer,
    setListAll,
} from '../../../../../../common/listReducer';
import { getSortAlphabeticFn } from '../../../../../../common/sorting';
import { prepareTableRules } from './proxySQLRulesHelper';

type ListParams = {
    page?: number;
    pageSize?: number;
    order?: (a: any, b: any) => any;
    filters?: Function[];
};

type FilterFunctionParams = ListParams & { arr?: any[] };
type RefreshFunctionParams = FetchRefreshFunctionParams;

export type UseProxySQLRulesListProps = {
    pageSize?: number;
    clusterId: number | undefined;
    hostName: string | undefined;
    port: number | undefined;
    filters?: Function[];
};
export default function useProxySQLRules({
    pageSize = 15,
    clusterId,
    filters,
    hostName,
    port,
}: UseProxySQLRulesListProps) {
    const {
        loading,
        loaded,
        list: rulesList,
        refresh: refreshRules,
    } = useListFetch({
        name: 'queryRules',
        pageSize,
        fetchFn: async ({ pageSize, page, ...rest }, opts) => {
            const response = await CmonProxysqlService.queryrules(
                {
                    cluster_id: clusterId,
                    hostName: hostName,
                    port: port,
                    ...rest,
                },
                opts
            );

            return {
                list: prepareTableRules(response.queryResults),
                total: response.queryResultTotalCount,
            };
        },
        cancelFn: async ({ requestId }) => {
            await CmonProxysqlService.cancelRequest(requestId);
        },
    });

    const [list, setList] = useState<any[]>();
    const [total, setTotal] = useState<number>(0);

    const [
        {
            page: listPage,
            pageSize: listPageSize,
            order: listOrder,
            filters: listFilters,
        },
        listDispatch,
    ] = useReducer(listReducer, {
        ...initialListState,
        pageSize: pageSize || 0,
        filters: filters,
        order: getSortAlphabeticFn('ascend', (x) => x.rule),
    });

    const filter = useCallback<(p?: FilterFunctionParams) => void>(
        ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            arr = rulesList || [],
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            const filteredArr = arrayFilter({
                filters: [...(filters || [])],
                arr,
            });

            setList([
                ...arrayPages({
                    page,
                    pageSize,
                    order,
                    arr: filteredArr,
                }),
            ]);
            setTotal(filteredArr.length);
        },

        [listPage, listPageSize, listOrder, listFilters, rulesList]
    );
    const refresh = useCallback<(p?: RefreshFunctionParams) => Promise<void>>(
        async ({
            page = listPage,
            pageSize = listPageSize,
            order = listOrder,
            filters = listFilters,
            ...rest
        } = {}) => {
            listDispatch(setListAll({ page, pageSize, order, filters }));
            await refreshRules({
                ...rest,
            });
        },
        []
    );

    const [rulesIds, setRulesIds] = useState<number[]>();

    useEffect(() => {
        if (Array.isArray(rulesList)) {
            const ids = rulesList.map((element: any) => {
                return +element.rule.replace('Rule', '');
            });
            setRulesIds(ids);
            filter({
                arr: rulesList,
            });
        }
    }, [rulesList]);

    return {
        list,
        rulesIds,
        loading,
        loaded,
        refresh,
        filter,
        page: listPage,
        pageSize: listPageSize,
        total,
    };
}
